import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import {
  useProjectByIdQuery,
  useDeleteProject,
  useProjectFilter,
  useRotateProjectApiKey,
  useTestProjectPipelineWebhook,
  useUpdateProject,
  useWipeProjectData,
} from '@/features/projects';
import { useMetricThresholdDrafts } from '@/features/projects/hooks/useMetricThresholdDrafts';
import {
  defaultProjectMetricThresholds,
  mergeProjectThresholdGroup,
} from '@/features/projects/utils/projectMetricThresholds';
import {
  missingProjectRedirectPath,
  resolveProjectSettingsProjectId,
} from '@/features/projects/utils/projectSettingsRoute';
import { useNotifications } from '@/providers/notificationHooks';
import { useCurrentOrgRole, useMutationErrorSnackbar } from '@/shared/hooks';
import { resolveProjectManagementAccess } from './projectManagementAccess';
import type { AuthSession } from '@/auth/types';
import type { ProjectBadgeColor } from '@flowqa/shared';

export type ProjectSettingsViewModel = {
  projectQuery: ReturnType<typeof useProjectByIdQuery>;
  project: ReturnType<typeof useProjectByIdQuery>['data'] | null;
  updateProject: ReturnType<typeof useUpdateProject>;
  rotateProjectApiKey: ReturnType<typeof useRotateProjectApiKey>;
  deleteProject: ReturnType<typeof useDeleteProject>;
  testProjectPipelineWebhook: ReturnType<typeof useTestProjectPipelineWebhook>;
  wipeProjectData: ReturnType<typeof useWipeProjectData>;
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
  canManageProject: boolean;
  canManageProjectLoading: boolean;
  nameValue: string;
  setNameValue: (value: string) => void;
  nameDirty: boolean;
  setNameDirty: (value: boolean) => void;
  badgeEnabledValue: boolean;
  setBadgeEnabledValue: (value: boolean) => void;
  badgeCharsValue: string;
  setBadgeCharsValue: (value: string) => void;
  badgeColorValue: ProjectBadgeColor;
  setBadgeColorValue: (value: ProjectBadgeColor) => void;
  apiKeyValue: string;
  setApiKeyValue: (value: string) => void;
  revokeOpen: boolean;
  setRevokeOpen: (open: boolean) => void;
  mutationError: ReturnType<typeof useMutationErrorSnackbar>;
  apiKeyError: ReturnType<typeof useMutationErrorSnackbar>;
  suiteThresholdDrafts: ReturnType<typeof useMetricThresholdDrafts>['drafts'];
  setSuiteThresholdDrafts: ReturnType<typeof useMetricThresholdDrafts>['setDrafts'];
  suitePreferredUnitsRef: ReturnType<typeof useMetricThresholdDrafts>['preferredUnitsRef'];
  resetSuiteThresholdDrafts: ReturnType<typeof useMetricThresholdDrafts>['resetDrafts'];
  suiteThresholdsDirty: ReturnType<typeof useMetricThresholdDrafts>['isDirty'];
  suiteThresholdsValid: ReturnType<typeof useMetricThresholdDrafts>['isValid'];
  testThresholdDrafts: ReturnType<typeof useMetricThresholdDrafts>['drafts'];
  setTestThresholdDrafts: ReturnType<typeof useMetricThresholdDrafts>['setDrafts'];
  testPreferredUnitsRef: ReturnType<typeof useMetricThresholdDrafts>['preferredUnitsRef'];
  resetTestThresholdDrafts: ReturnType<typeof useMetricThresholdDrafts>['resetDrafts'];
  testThresholdsDirty: ReturnType<typeof useMetricThresholdDrafts>['isDirty'];
  testThresholdsValid: ReturnType<typeof useMetricThresholdDrafts>['isValid'];
  executionRunThresholdDrafts: ReturnType<typeof useMetricThresholdDrafts>['drafts'];
  setExecutionRunThresholdDrafts: ReturnType<typeof useMetricThresholdDrafts>['setDrafts'];
  executionRunPreferredUnitsRef: ReturnType<typeof useMetricThresholdDrafts>['preferredUnitsRef'];
  resetExecutionRunThresholdDrafts: ReturnType<typeof useMetricThresholdDrafts>['resetDrafts'];
  executionRunThresholdsDirty: ReturnType<typeof useMetricThresholdDrafts>['isDirty'];
  executionRunThresholdsValid: ReturnType<typeof useMetricThresholdDrafts>['isValid'];
  pipelineThresholdDrafts: ReturnType<typeof useMetricThresholdDrafts>['drafts'];
  setPipelineThresholdDrafts: ReturnType<typeof useMetricThresholdDrafts>['setDrafts'];
  pipelinePreferredUnitsRef: ReturnType<typeof useMetricThresholdDrafts>['preferredUnitsRef'];
  resetPipelineThresholdDrafts: ReturnType<typeof useMetricThresholdDrafts>['resetDrafts'];
  pipelineThresholdsDirty: ReturnType<typeof useMetricThresholdDrafts>['isDirty'];
  pipelineThresholdsValid: ReturnType<typeof useMetricThresholdDrafts>['isValid'];
  stageThresholdDrafts: ReturnType<typeof useMetricThresholdDrafts>['drafts'];
  setStageThresholdDrafts: ReturnType<typeof useMetricThresholdDrafts>['setDrafts'];
  stagePreferredUnitsRef: ReturnType<typeof useMetricThresholdDrafts>['preferredUnitsRef'];
  resetStageThresholdDrafts: ReturnType<typeof useMetricThresholdDrafts>['resetDrafts'];
  stageThresholdsDirty: ReturnType<typeof useMetricThresholdDrafts>['isDirty'];
  stageThresholdsValid: ReturnType<typeof useMetricThresholdDrafts>['isValid'];
  jobThresholdDrafts: ReturnType<typeof useMetricThresholdDrafts>['drafts'];
  setJobThresholdDrafts: ReturnType<typeof useMetricThresholdDrafts>['setDrafts'];
  jobPreferredUnitsRef: ReturnType<typeof useMetricThresholdDrafts>['preferredUnitsRef'];
  resetJobThresholdDrafts: ReturnType<typeof useMetricThresholdDrafts>['resetDrafts'];
  jobThresholdsDirty: ReturnType<typeof useMetricThresholdDrafts>['isDirty'];
  jobThresholdsValid: ReturnType<typeof useMetricThresholdDrafts>['isValid'];
  notify: ReturnType<typeof useNotifications>['notify'];
  navigate: ReturnType<typeof useNavigate>;
};

export const useProjectSettingsViewModel = (
  session: AuthSession | null,
): ProjectSettingsViewModel => {
  const location = useLocation();
  const navigate = useNavigate();
  const mutationError = useMutationErrorSnackbar();
  const apiKeyError = useMutationErrorSnackbar();
  const { notify } = useNotifications();
  const updateProject = useUpdateProject();
  const rotateProjectApiKey = useRotateProjectApiKey();
  const deleteProject = useDeleteProject();
  const testProjectPipelineWebhook = useTestProjectPipelineWebhook();
  const wipeProjectData = useWipeProjectData();
  const { selectedOrganisationId, setSelectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId: persistedProjectId, setSelectedProjectId } = useProjectFilter();
  const routeProjectId = resolveProjectSettingsProjectId(location.search);
  const selectedProjectId = routeProjectId ?? persistedProjectId;
  const [nameValue, setNameValue] = useState('');
  const [nameDirty, setNameDirty] = useState(false);
  const [badgeEnabledValue, setBadgeEnabledValue] = useState(true);
  const [badgeCharsValue, setBadgeCharsValue] = useState('');
  const [badgeColorValue, setBadgeColorValue] = useState<ProjectBadgeColor>('teal');
  const [apiKeyValue, setApiKeyValue] = useState('');
  const [revokeOpen, setRevokeOpen] = useState(false);
  const projectQuery = useProjectByIdQuery(selectedProjectId);
  const project = projectQuery.data ?? null;
  const { isAdmin: isOrgAdmin, isLoading: isOrgRoleLoading } = useCurrentOrgRole(session?.userId, {
    organisationId: project?.organisationId ?? null,
  });
  const { canManageProject, canManageProjectLoading } = resolveProjectManagementAccess({
    session,
    projectOrganisationId: project?.organisationId ?? null,
    projectCreatedById: project?.createdById ?? null,
    isOrganisationAdmin: isOrgAdmin,
    isOrganisationRoleLoading: isOrgRoleLoading,
  });
  const suiteThresholdSource = project
    ? mergeProjectThresholdGroup(
        defaultProjectMetricThresholds.suites,
        project.metricThresholds.suites,
      )
    : defaultProjectMetricThresholds.suites;
  const testThresholdSource = project
    ? mergeProjectThresholdGroup(
        defaultProjectMetricThresholds.tests,
        project.metricThresholds.tests,
      )
    : defaultProjectMetricThresholds.tests;
  const executionRunThresholdSource = project
    ? mergeProjectThresholdGroup(
        defaultProjectMetricThresholds.executionRuns,
        project.metricThresholds.executionRuns,
      )
    : defaultProjectMetricThresholds.executionRuns;
  const pipelineThresholdSource = project
    ? mergeProjectThresholdGroup(
        defaultProjectMetricThresholds.pipelines,
        project.metricThresholds.pipelines,
      )
    : defaultProjectMetricThresholds.pipelines;
  const stageThresholdSource = project
    ? mergeProjectThresholdGroup(
        defaultProjectMetricThresholds.stages,
        project.metricThresholds.stages,
      )
    : defaultProjectMetricThresholds.stages;
  const jobThresholdSource = project
    ? mergeProjectThresholdGroup(defaultProjectMetricThresholds.jobs, project.metricThresholds.jobs)
    : defaultProjectMetricThresholds.jobs;
  const {
    drafts: suiteThresholdDrafts,
    setDrafts: setSuiteThresholdDrafts,
    preferredUnitsRef: suitePreferredUnitsRef,
    resetDrafts: resetSuiteThresholdDrafts,
    isDirty: suiteThresholdsDirty,
    isValid: suiteThresholdsValid,
  } = useMetricThresholdDrafts(suiteThresholdSource);
  const {
    drafts: testThresholdDrafts,
    setDrafts: setTestThresholdDrafts,
    preferredUnitsRef: testPreferredUnitsRef,
    resetDrafts: resetTestThresholdDrafts,
    isDirty: testThresholdsDirty,
    isValid: testThresholdsValid,
  } = useMetricThresholdDrafts(testThresholdSource);
  const {
    drafts: executionRunThresholdDrafts,
    setDrafts: setExecutionRunThresholdDrafts,
    preferredUnitsRef: executionRunPreferredUnitsRef,
    resetDrafts: resetExecutionRunThresholdDrafts,
    isDirty: executionRunThresholdsDirty,
    isValid: executionRunThresholdsValid,
  } = useMetricThresholdDrafts(executionRunThresholdSource);
  const {
    drafts: pipelineThresholdDrafts,
    setDrafts: setPipelineThresholdDrafts,
    preferredUnitsRef: pipelinePreferredUnitsRef,
    resetDrafts: resetPipelineThresholdDrafts,
    isDirty: pipelineThresholdsDirty,
    isValid: pipelineThresholdsValid,
  } = useMetricThresholdDrafts(pipelineThresholdSource);
  const {
    drafts: stageThresholdDrafts,
    setDrafts: setStageThresholdDrafts,
    preferredUnitsRef: stagePreferredUnitsRef,
    resetDrafts: resetStageThresholdDrafts,
    isDirty: stageThresholdsDirty,
    isValid: stageThresholdsValid,
  } = useMetricThresholdDrafts(stageThresholdSource);
  const {
    drafts: jobThresholdDrafts,
    setDrafts: setJobThresholdDrafts,
    preferredUnitsRef: jobPreferredUnitsRef,
    resetDrafts: resetJobThresholdDrafts,
    isDirty: jobThresholdsDirty,
    isValid: jobThresholdsValid,
  } = useMetricThresholdDrafts(jobThresholdSource);

  useEffect(() => {
    if (projectQuery.isFetched && project === null) {
      void navigate(missingProjectRedirectPath, {
        replace: true,
        state: { notice: 'Project not found in this workspace' },
      });
    }
  }, [navigate, project, projectQuery.isFetched]);

  useEffect(() => {
    if (routeProjectId === null) return;
    if (!project?.id) return;
    if (persistedProjectId === project.id) return;
    setSelectedProjectId(project.id);
  }, [persistedProjectId, project?.id, routeProjectId, setSelectedProjectId]);

  useEffect(() => {
    if (!project) return;
    const nextOrgId = project.organisationId;
    if (selectedOrganisationId !== nextOrgId) {
      setSelectedOrganisationId(nextOrgId);
    }
  }, [project, selectedOrganisationId, setSelectedOrganisationId]);

  useEffect(() => {
    if (!project) return;
    setNameValue(project.name);
    setBadgeEnabledValue(project.badgeEnabled);
    setBadgeCharsValue(project.badgeChars);
    setBadgeColorValue(project.badgeColor);
    setNameDirty(false);
  }, [project?.badgeChars, project?.badgeColor, project?.badgeEnabled, project?.name]);

  useEffect(() => {
    if (!project) return;
    setApiKeyValue('');
  }, [project?.id, setApiKeyValue]);

  useEffect(() => {
    if (!project) return;
    resetSuiteThresholdDrafts(
      mergeProjectThresholdGroup(
        defaultProjectMetricThresholds.suites,
        project.metricThresholds.suites,
      ),
    );
    resetTestThresholdDrafts(
      mergeProjectThresholdGroup(
        defaultProjectMetricThresholds.tests,
        project.metricThresholds.tests,
      ),
    );
    resetExecutionRunThresholdDrafts(
      mergeProjectThresholdGroup(
        defaultProjectMetricThresholds.executionRuns,
        project.metricThresholds.executionRuns,
      ),
    );
    resetPipelineThresholdDrafts(
      mergeProjectThresholdGroup(
        defaultProjectMetricThresholds.pipelines,
        project.metricThresholds.pipelines,
      ),
    );
    resetStageThresholdDrafts(
      mergeProjectThresholdGroup(
        defaultProjectMetricThresholds.stages,
        project.metricThresholds.stages,
      ),
    );
    resetJobThresholdDrafts(
      mergeProjectThresholdGroup(
        defaultProjectMetricThresholds.jobs,
        project.metricThresholds.jobs,
      ),
    );
  }, [
    project,
    resetExecutionRunThresholdDrafts,
    resetJobThresholdDrafts,
    resetPipelineThresholdDrafts,
    resetStageThresholdDrafts,
    resetSuiteThresholdDrafts,
    resetTestThresholdDrafts,
  ]);

  return {
    projectQuery,
    project,
    updateProject,
    rotateProjectApiKey,
    deleteProject,
    testProjectPipelineWebhook,
    wipeProjectData,
    selectedOrganisationId,
    selectedProjectId,
    canManageProject,
    canManageProjectLoading,
    nameValue,
    setNameValue,
    nameDirty,
    setNameDirty,
    badgeCharsValue,
    setBadgeCharsValue,
    badgeColorValue,
    setBadgeColorValue,
    badgeEnabledValue,
    setBadgeEnabledValue,
    apiKeyValue,
    setApiKeyValue,
    revokeOpen,
    setRevokeOpen,
    mutationError,
    apiKeyError,
    suiteThresholdDrafts,
    setSuiteThresholdDrafts,
    suitePreferredUnitsRef,
    resetSuiteThresholdDrafts,
    suiteThresholdsDirty,
    suiteThresholdsValid,
    testThresholdDrafts,
    setTestThresholdDrafts,
    testPreferredUnitsRef,
    resetTestThresholdDrafts,
    testThresholdsDirty,
    testThresholdsValid,
    executionRunThresholdDrafts,
    setExecutionRunThresholdDrafts,
    executionRunPreferredUnitsRef,
    resetExecutionRunThresholdDrafts,
    executionRunThresholdsDirty,
    executionRunThresholdsValid,
    pipelineThresholdDrafts,
    setPipelineThresholdDrafts,
    pipelinePreferredUnitsRef,
    resetPipelineThresholdDrafts,
    pipelineThresholdsDirty,
    pipelineThresholdsValid,
    stageThresholdDrafts,
    setStageThresholdDrafts,
    stagePreferredUnitsRef,
    resetStageThresholdDrafts,
    stageThresholdsDirty,
    stageThresholdsValid,
    jobThresholdDrafts,
    setJobThresholdDrafts,
    jobPreferredUnitsRef,
    resetJobThresholdDrafts,
    jobThresholdsDirty,
    jobThresholdsValid,
    notify,
    navigate,
  };
};
